import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLeadForm } from '../../../contexts/LeadFormContext';

const ComparisonPanel = ({ products, isOpen, onClose, onRemoveProduct, onClearAll }) => {
  const { openLeadForm } = useLeadForm();
  
  if (!isOpen || !products || products?.length === 0) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(price);
  };

  const handleWhatsAppContact = (selectedProducts) => {
    openLeadForm();
  };

  const getComparisonAttributes = () => {
    const attributes = new Set();
    products?.forEach(product => {
      if (product?.specifications) {
        Object.keys(product?.specifications)?.forEach(key => attributes?.add(key));
      }
    });
    return Array.from(attributes);
  };

  const comparisonAttributes = getComparisonAttributes();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl max-w-7xl w-full max-h-[90vh] overflow-hidden shadow-premium-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="font-playfair font-bold text-2xl text-text-primary">
              Comparar Produtos
            </h2>
            <p className="text-text-secondary mt-1">
              Compare até 3 produtos lado a lado
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onClearAll}
              iconName="Trash2"
              iconPosition="left"
            >
              Limpar Tudo
            </Button>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-muted hover:bg-border flex items-center justify-center transition-colors duration-200"
            >
              <Icon name="X" size={20} className="text-text-primary" />
            </button>
          </div>
        </div>

        {/* Comparison Content */}
        <div className="overflow-auto max-h-[calc(90vh-140px)]">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products?.map((product) => (
                <div key={product?.id} className="bg-muted rounded-xl overflow-hidden">
                  {/* Product Image */}
                  <div className="relative aspect-[4/3] bg-background">
                    <Image
                      src={product?.images?.[0] || product?.image}
                      alt={product?.name}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => onRemoveProduct(product?.id)}
                      className="absolute top-3 right-3 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all duration-200"
                    >
                      <Icon name="X" size={16} className="text-text-primary" />
                    </button>
                  </div>

                  {/* Product Info */}
                  <div className="p-4 space-y-4">
                    {/* Basic Info */}
                    <div>
                      <div className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1">
                        {product?.category}
                      </div>
                      <h3 className="font-playfair font-semibold text-lg text-text-primary mb-2">
                        {product?.name}
                      </h3>
                      <div className="font-bold text-xl text-text-primary">
                        {product?.priceRange ? 
                          `${formatPrice(product?.priceRange?.min)} - ${formatPrice(product?.priceRange?.max)}` :
                          formatPrice(product?.price)
                        }
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-text-secondary">Madeira</div>
                        <div className="font-medium text-text-primary">{product?.woodType}</div>
                      </div>
                      <div>
                        <div className="text-text-secondary">Dimensões</div>
                        <div className="font-medium text-text-primary">{product?.dimensions}</div>
                      </div>
                      <div>
                        <div className="text-text-secondary">Entrega</div>
                        <div className="font-medium text-text-primary">{product?.deliveryTime}</div>
                      </div>
                      <div>
                        <div className="text-text-secondary">Garantia</div>
                        <div className="font-medium text-text-primary">{product?.warranty}</div>
                      </div>
                    </div>

                    {/* Features */}
                    {product?.features && product?.features?.length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-text-primary mb-2">Características</div>
                        <div className="space-y-1">
                          {product?.features?.slice(0, 4)?.map((feature, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <Icon name="Check" size={12} className="text-success" />
                              <span className="text-xs text-text-secondary">{feature}</span>
                            </div>
                          ))}
                          {product?.features?.length > 4 && (
                            <div className="text-xs text-text-secondary">
                              +{product?.features?.length - 4} mais características
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Specifications */}
                    {product?.specifications && (
                      <div>
                        <div className="text-sm font-medium text-text-primary mb-2">Especificações</div>
                        <div className="space-y-1">
                          {comparisonAttributes?.slice(0, 5)?.map((attr) => (
                            <div key={attr} className="flex justify-between text-xs">
                              <span className="text-text-secondary">{attr}:</span>
                              <span className="text-text-primary font-medium">
                                {product?.specifications?.[attr] || 'N/A'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="MessageCircle"
                      iconPosition="left"
                      onClick={() => handleWhatsAppContact([product])}
                      className="w-full"
                    >
                      Consultar Produto
                    </Button>
                  </div>
                </div>
              ))}

              {/* Add More Placeholder */}
              {products?.length < 3 && (
                <div className="bg-muted rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center p-8 text-center min-h-[400px]">
                  <div className="w-16 h-16 bg-background rounded-full flex items-center justify-center mb-4">
                    <Icon name="Plus" size={24} className="text-text-secondary" />
                  </div>
                  <h3 className="font-semibold text-text-primary mb-2">
                    Adicionar Produto
                  </h3>
                  <p className="text-sm text-text-secondary mb-4">
                    Você pode comparar até 3 produtos. Adicione mais produtos para uma comparação completa.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onClose}
                  >
                    Voltar ao Catálogo
                  </Button>
                </div>
              )}
            </div>

            {/* Detailed Comparison Table */}
            {products?.length > 1 && comparisonAttributes?.length > 0 && (
              <div className="mt-8">
                <h3 className="font-playfair font-semibold text-xl text-text-primary mb-4">
                  Comparação Detalhada
                </h3>
                <div className="bg-background rounded-xl border border-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left p-4 font-medium text-text-primary">Especificação</th>
                          {products?.map((product) => (
                            <th key={product?.id} className="text-left p-4 font-medium text-text-primary min-w-[200px]">
                              {product?.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonAttributes?.map((attr, index) => (
                          <tr key={attr} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                            <td className="p-4 font-medium text-text-secondary">{attr}</td>
                            {products?.map((product) => (
                              <td key={product?.id} className="p-4 text-text-primary">
                                {product?.specifications?.[attr] || 'N/A'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Continuar Navegando
            </Button>
            <Button
              variant="default"
              iconName="MessageCircle"
              iconPosition="left"
              onClick={() => handleWhatsAppContact(products)}
              className="flex-1 bg-gradient-forest"
            >
              Consultar Todos os Produtos
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonPanel;